import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizAPI } from '../services/api';
import { cookieUtils } from '../utils/cookies';

const QuizPage = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const s = cookieUtils.getStudentData();
    if (!s) {
      navigate('/');
      return;
    }
    setStudentData(s);
    loadQuestions();

    const saved = cookieUtils.getProgress();
    if (saved) {
      if (typeof saved.currentQuestionIndex === 'number') {
        setCurrentQuestionIndex(saved.currentQuestionIndex);
      }
      if (typeof saved.timeLeft === 'number') {
        setTimeLeft(saved.timeLeft);
      }
      if (saved.selectedAnswers) {
        setSelectedAnswers(saved.selectedAnswers);
      }
    }
  }, [navigate]);

  // Format JS Date to backend LocalDateTime string (yyyy-MM-dd'T'HH:mm:ss)
  const formatLocalDateTime = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Single submit handler
  const handleSubmitQuiz = useCallback(async () => {
    if (submitting) return;
    if (!studentData) return;

    setSubmitting(true);
    try {
      // Calculate score
      let score = 0;
      questions.forEach(question => {
        if (selectedAnswers[question.id] === question.correctAnswer) {
          score++;
        }
      });

      // Update student data
      const updatedStudent = {
        ...studentData,
        score: score,
        status: 'completed',
        endTime: formatLocalDateTime(new Date()),
        answers: JSON.stringify(selectedAnswers)
      };

      await quizAPI.updateStudent(updatedStudent);
      cookieUtils.setStudentData(updatedStudent);
      cookieUtils.clearProgress();
      navigate('/results');
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      setSubmitting(false);
    }
  }, [submitting, questions, selectedAnswers, studentData, navigate]);

  // Timer effect (placed after handler definition)
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      handleSubmitQuiz();
    }
  }, [timeLeft, handleSubmitQuiz]);

  // persist progress on significant changes
  useEffect(() => {
    cookieUtils.setProgress({
      inProgress: true,
      currentQuestionIndex,
      timeLeft,
      selectedAnswers
    });
  }, [currentQuestionIndex, timeLeft, selectedAnswers]);

  const loadQuestions = async () => {
    try {
      const response = await quizAPI.getQuestions();
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load questions:', error);
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center' }}>
            <h2>Loading Quiz...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center' }}>
            <h2>No questions available</h2>
            <button className="btn" onClick={() => navigate('/')}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const answeredQuestions = Object.keys(selectedAnswers).length;

  return (
    <div className="container">
      <div className="timer">
        Time Left: {formatTime(timeLeft)}
      </div>

      <div className="card">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="quiz-question">
          <h2>
            Question {currentQuestionIndex + 1} of {questions.length}
            <span style={{ 
              fontSize: '14px', 
              fontWeight: 'normal', 
              color: '#666',
              marginLeft: '10px'
            }}>
              ({currentQuestion.category === 'theory' ? 'Theory' : 'Case Study'})
            </span>
          </h2>
          
          <p style={{ 
            fontSize: '18px', 
            marginBottom: '20px',
            lineHeight: '1.6'
          }}>
            {currentQuestion.questionText}
          </p>

          <div className="quiz-options">
            {['A', 'B', 'C', 'D'].map(option => {
              const optionText = currentQuestion[`option${option}`];
              const isSelected = selectedAnswers[currentQuestion.id] === option.toLowerCase();
              
              return (
                <button
                  key={option}
                  className={`quiz-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleAnswerSelect(currentQuestion.id, option.toLowerCase())}
                >
                  <strong>{option}.</strong> {optionText}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: '30px'
        }}>
          <button 
            className="btn btn-secondary"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </button>

          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#666', marginBottom: '10px' }}>
              Answered: {answeredQuestions} / {questions.length}
            </p>
            {isLastQuestion ? (
              <button 
                className="btn btn-success"
                onClick={handleSubmitQuiz}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Finish Quiz'}
              </button>
            ) : (
              <button 
                className="btn"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
